import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Container,
  Grid,
  Card,
  CardContent,
  Fade,
  Zoom,
  Slide,
  Stack,
  Chip,
  Avatar,
  Button,
  TextField,
  GlobalStyles
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import CognitoService from '../services/cognitoService';
import {
  Code as CodeIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Groups as GroupsIcon,
  SmartToy as SmartToyIcon,
  Shield as ShieldIcon,
  Star as StarIcon,
  Edit as EditIcon,
  Mic as MicIcon,
  AutoAwesome as AutoAwesomeIcon,
  Description as DescriptionIcon,
  VideoCall as VideoCallIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';

// Advanced animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(2deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const slideInUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// Global styles - cleaner approach
const globalStyles = (
  <GlobalStyles
    styles={{
      '@keyframes float': {
        '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
        '50%': { transform: 'translateY(-20px) rotate(2deg)' },
      },
      '@keyframes shimmer': {
        '0%': { backgroundPosition: '-200% 0' },
        '100%': { backgroundPosition: '200% 0' },
      },
    }}
  />
);

// Enhanced styled components
const HeroContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  maxHeight: '100vh',
  background: `
    radial-gradient(circle at 20% 80%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, ${alpha(theme.palette.secondary.main, 0.15)} 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, ${alpha(theme.palette.info.main, 0.1)} 0%, transparent 50%),
    linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)
  `,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${theme.palette.primary.main.slice(1)}' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
    `,
    opacity: 0.3,
  },
}));

const GlassPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 24,
  backdropFilter: 'blur(20px)',
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.9)} 0%, 
    ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  boxShadow: `
    0 20px 40px ${alpha(theme.palette.common.black, 0.3)},
    0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)},
    inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}
  `,
  overflow: 'hidden',
  position: 'relative',
  padding: theme.spacing(4),
  maxWidth: 420,
  width: '100%',
  animation: `${slideInUp} 0.8s ease-out`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
    animation: `${shimmer} 3s infinite`,
  },
}));

const BrandLogo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  position: 'relative',
}));

const LogoIcon = styled(SmartToyIcon)(({ theme }) => ({
  fontSize: 48,
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1),
  animation: `${float} 6s ease-in-out infinite`,
  filter: 'drop-shadow(0 4px 8px rgba(99, 102, 241, 0.3))',
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '2.5rem',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  letterSpacing: '-1px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -4,
    left: 0,
    right: 0,
    height: 2,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: 1,
    opacity: 0.6,
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.6)} 0%, 
    ${alpha(theme.palette.background.paper, 0.3)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.common.black, 0.2)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

// Custom Google Button - cleaner approach
const CustomGoogleButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: 56,
  borderRadius: 16,
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.9)} 0%, 
    ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  color: theme.palette.text.primary,
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
  
  '&:hover': {
    border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.2)}`,
    background: `linear-gradient(145deg, 
      ${alpha(theme.palette.background.paper, 1)} 0%, 
      ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
  },
  
  '&:disabled': {
    opacity: 0.7,
    cursor: 'not-allowed',
    transform: 'none',
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
    transition: 'left 0.5s',
  },
  
  '&:hover::before': {
    left: '100%',
  },
}));

function LoginFixed() {
  const theme = useTheme();
  const navigate = useNavigate();
  const auth = useAuth() || { login: () => {}, isAuthenticated: false, loading: false };
  const { login, isAuthenticated, loading: authLoading } = auth;
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [mounted, setMounted] = useState(false);
  
  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Ensure component is properly mounted
  useEffect(() => {
    setMounted(true);
  }, []);

  // Show features after initial load
  useEffect(() => {
    const timer = setTimeout(() => setShowFeatures(true), 500);
    return () => clearTimeout(timer);
  }, []);
  
  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');
    try {
      await login(credentialResponse.credential);
      navigate('/');
    } catch (err) {
      setError('Failed to login. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleError = (error) => {
    setError('Google Sign-In failed. Please try again.');
    setLoading(false);
  };

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    setError('');
    try {
      // This will redirect to Cognito hosted UI with Microsoft login
      await CognitoService.loginWithMicrosoft();
      // User will be redirected, so this code won't execute
    } catch (err) {
      setError('Microsoft Sign-In failed. Please try again.');
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <CodeIcon />,
      title: 'PAI Snip',
      description: 'Intelligent code snippet management with AI-powered insights and syntax highlighting',
      color: theme.palette.primary.main,
    },
    {
      icon: <DescriptionIcon />,
      title: 'PAI Doc',
      description: 'Smart document editor with writing assistance, grammar checking, and content suggestions',
      color: theme.palette.success.main,
    },
  ];

  const stats = [
    { number: '50K+', label: 'Active Users', icon: <GroupsIcon /> },
    { number: '1M+', label: 'AI Interactions', icon: <AutoAwesomeIcon /> },
    { number: '99.9%', label: 'Uptime', icon: <ShieldIcon /> },
  ];

  return (
    <>
      {globalStyles}
      <HeroContainer>
        <Container maxWidth="lg" sx={{ height: '100vh', display: 'flex', alignItems: 'center' }}>
          <Grid container spacing={4} alignItems="center" sx={{ height: '100%' }}>
            {/* Left side - Login Form */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Fade in timeout={800}>
                <GlassPaper elevation={0}>
                  <BrandLogo>
                    <LogoIcon />
                    <LogoText variant="h1">
                      Pastely
                    </LogoText>
                  </BrandLogo>
                  
                  <Box textAlign="center" mb={3}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
                      Welcome to the Future
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.95rem', lineHeight: 1.5, mb: 2 }}>
                      Your AI-powered workspace for code, documents, meetings, and intelligent assistance
                    </Typography>
                    <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" gap={0.5}>
                      <Chip 
                        icon={<CodeIcon />} 
                        label="PAI Snip" 
                        size="small" 
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }} 
                      />
                      <Chip 
                        icon={<EditIcon />} 
                        label="PAI Doc" 
                        size="small" 
                        sx={{ 
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }} 
                      />
                    </Stack>
                  </Box>

                  {error && (
                    <Zoom in>
                      <Alert 
                        severity="error" 
                        sx={{ 
                          mb: 3, 
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          background: alpha(theme.palette.error.main, 0.1),
                          border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                        }}
                      >
                        {error}
                      </Alert>
                    </Zoom>
                  )}
                  
                  <Box sx={{ mb: 3 }}>
                    {!mounted || loading || authLoading ? (
                      <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                        <CircularProgress size={28} thickness={4} sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="body2" sx={{ ml: 2, fontWeight: 500 }}>
                          Signing you in securely...
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        {/* Google Login - Original but styled */}
                        <Box sx={{
                          '& > div': {
                            width: '100% !important',
                          },
                          '& div[role="button"]': {
                            width: '100% !important',
                            height: '48px !important',
                            borderRadius: '8px !important',
                            border: `1px solid ${alpha(theme.palette.divider, 0.3)} !important`,
                            backgroundColor: `${alpha(theme.palette.background.paper, 0.8)} !important`,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
                            '&:hover': {
                              borderColor: `${theme.palette.primary.main} !important`,
                              backgroundColor: `${alpha(theme.palette.primary.main, 0.05)} !important`,
                              transform: 'translateY(-1px) !important',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)} !important`,
                            },
                          }
                        }}>
                          <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            useOneTap={false}
                            theme="outline"
                            text="continue_with"
                            shape="rectangular"
                            size="large"
                            auto_select={false}
                          />
                        </Box>

                        {/* Enterprise Login */}
                        <Button
                          onClick={handleMicrosoftLogin}
                          disabled={loading}
                          fullWidth
                          variant="outlined"
                          size="large"
                          startIcon={<BusinessIcon />}
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            borderColor: alpha(theme.palette.divider, 0.3),
                            color: theme.palette.text.primary,
                            backgroundColor: alpha(theme.palette.background.paper, 0.8),
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          Sign in with Organization
                        </Button>

                        {/* Divider */}
                        <Divider sx={{ my: 1, opacity: 0.3 }}>
                          <Typography variant="caption" color="textSecondary">
                            or
                          </Typography>
                        </Divider>

                        {/* Guest Access - Highlighted */}
                        <Button
                          onClick={() => {
                            auth.loginAsGuest?.();
                            navigate('/');
                          }}
                          fullWidth
                          variant="contained"
                          size="large"
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            color: theme.palette.secondary.main,
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.secondary.main, 0.15),
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.2)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          Continue as Guest
                        </Button>
                      </Stack>
                    )}
                  </Box>

                  <Divider sx={{ my: 2, opacity: 0.3 }}>
                    <Typography variant="caption" color="textSecondary">
                      Trusted by developers worldwide
                    </Typography>
                  </Divider>

                  {/* Trust indicators */}
                  <Stack direction="row" spacing={2} justifyContent="center" mb={2}>
                    {stats.map((stat, index) => (
                      <Fade in={showFeatures} timeout={1000 + index * 200} key={stat.label}>
                        <Box textAlign="center">
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                            {stat.number}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                            {stat.label}
                          </Typography>
                        </Box>
                      </Fade>
                    ))}
                  </Stack>
                  
                  <Typography variant="caption" color="textSecondary" align="center" display="block" sx={{ fontSize: '0.7rem' }}>
                    By signing in, you agree to our{' '}
                    <Box component="span" sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}>
                      Terms of Service
                    </Box>{' '}
                    and{' '}
                    <Box component="span" sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}>
                      Privacy Policy
                    </Box>
                  </Typography>
                </GlassPaper>
              </Fade>
            </Grid>

            {/* Right side - Features */}
            <Grid item xs={12} md={6} sx={{ height: '100vh', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ pl: { md: 2 }, width: '100%', maxHeight: '90vh', overflow: 'hidden' }}>
                <Slide in={showFeatures} direction="left" timeout={1000}>
                  <Box>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, mb: 2 }}>
                      Your Complete{' '}
                      <Box component="span" sx={{ 
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}>
                        AI Workspace
                      </Box>
                    </Typography>
                    
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontSize: '0.9rem', lineHeight: 1.4 }}>
                      From code snippets to meeting notes, from document writing to intelligent assistance - 
                      Pastely brings together all your productivity tools powered by advanced AI.
                    </Typography>
                    
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      {features.map((feature, index) => (
                        <Grid item xs={6} key={feature.title}>
                          <Fade in={showFeatures} timeout={1200 + index * 200}>
                            <FeatureCard>
                              <CardContent sx={{ p: 1.5 }}>
                                <Box display="flex" alignItems="center" mb={1}>
                                  <Avatar 
                                    sx={{ 
                                      bgcolor: alpha(feature.color, 0.1),
                                      color: feature.color,
                                      mr: 1,
                                      width: 32,
                                      height: 32,
                                    }}
                                  >
                                    {React.cloneElement(feature.icon, { sx: { fontSize: 18 } })}
                                  </Avatar>
                                </Box>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                  {feature.title}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                                  {feature.description}
                                </Typography>
                              </CardContent>
                            </FeatureCard>
                          </Fade>
                        </Grid>
                      ))}
                    </Grid>

                    {/* AI Capabilities Showcase - More compact */}
                    <Fade in={showFeatures} timeout={1800}>
                      <Box>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, mb: 1.5, fontSize: '1.1rem' }}>
                          Powered by Advanced AI
                        </Typography>
                        
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Box display="flex" alignItems="center" 
                                 sx={{ 
                                   p: 1.5, 
                                   borderRadius: 2, 
                                   background: alpha(theme.palette.primary.main, 0.05),
                                   border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                 }}>
                              <AutoAwesomeIcon sx={{ mr: 1.5, color: theme.palette.primary.main, fontSize: 18 }} />
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                                  Smart Code Analysis
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                                  Instant insights & optimization
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Box display="flex" alignItems="center" 
                                 sx={{ 
                                   p: 1.5, 
                                   borderRadius: 2, 
                                   background: alpha(theme.palette.success.main, 0.05),
                                   border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                                 }}>
                              <EditIcon sx={{ mr: 1.5, color: theme.palette.success.main, fontSize: 18 }} />
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                                  Writing Enhancement
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                                  Grammar & style improvement
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Fade>
                  </Box>
                </Slide>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </HeroContainer>
    </>
  );
}

export default LoginFixed;
